import { useEffect, useRef, useState } from 'react';

/**
 * Phase 2G PR 3 — Realtime presence hook.
 *
 * Subscribes to a WebSocket channel:
 *   - `game/{gameId}`   → live join counts + watcher counts
 *   - `squad/{squadId}` → squad chat presence + typing
 *
 * Returns the most recent payload of each `type`. Cleanly reconnects on
 * close (1s backoff). Token is appended as ?token=<jwt> query param.
 */
export default function useArenaxRealtime({ channel, channelId, enabled = true }) {
  const [snapshot, setSnapshot] = useState(null);
  const [presence, setPresence] = useState(null);     // {online_user_ids, online_count}
  const [watchers, setWatchers] = useState(null);       // number
  const [lastMessage, setLastMessage] = useState(null); // last 'squad_message' or 'game_progress'
  const [typing, setTyping] = useState(null);
  const [connected, setConnected] = useState(false);
  // Phase 2I — separate event streams for Realtime Phase 2
  const [lastReaction, setLastReaction] = useState(null);   // {emoji, user_name, at}
  const [matchProgress, setMatchProgress] = useState(null); // score/minute pulses
  const [squadActivity, setSquadActivity] = useState(null); // ambient activity events
  const [tournamentEvent, setTournamentEvent] = useState(null); // bracket-level updates
  // Phase 2L PR 5 — Spectator room commentary frames
  const [spectatorCommentary, setSpectatorCommentary] = useState(null);
  // Phase 2N PR 3 — Captain Hot Mic pin frames
  const [captainHotMic, setCaptainHotMic] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    if (!enabled || !channelId) return;
    let cancelled = false;

    const connect = () => {
      const token = localStorage.getItem('outplay_token');
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^https/, 'wss').replace(/^http/, 'ws');
      const url = `${base}/api/ws/arenax/${channel}/${channelId}?token=${encodeURIComponent(token || '')}`;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => { if (!cancelled) setConnected(true); };
        ws.onclose = () => {
          if (cancelled) return;
          setConnected(false);
          // Reconnect with 1.5s backoff
          reconnectRef.current = setTimeout(connect, 1500);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === 'snapshot') setSnapshot(msg);
            else if (msg.type === 'game_progress') setLastMessage(msg);
            else if (msg.type === 'watcher_count') setWatchers(msg.watchers);
            else if (msg.type === 'presence') setPresence(msg);
            else if (msg.type === 'squad_message') setLastMessage(msg);
            else if (msg.type === 'squad_typing') {
              setTyping(msg);
              setTimeout(() => setTyping(null), 3000);
            }
            // Phase 2I — Realtime Phase 2
            else if (msg.type === 'reaction') setLastReaction(msg);
            else if (msg.type === 'match_progress') setMatchProgress(msg);
            else if (msg.type === 'squad_activity') setSquadActivity(msg);
            else if (msg.type === 'match_completed') setTournamentEvent(msg);
            else if (msg.type === 'spectator_commentary') setSpectatorCommentary(msg);
            else if (msg.type === 'captain_hot_mic') setCaptainHotMic(msg);
          } catch {}
        };
      } catch (e) {
        // Silent — reconnect loop handles it
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    };
  }, [channel, channelId, enabled]);

  const send = (payload) => {
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      }
    } catch {}
  };

  return { snapshot, presence, watchers, lastMessage, typing, connected, send,
    lastReaction, matchProgress, squadActivity, tournamentEvent, spectatorCommentary,
    captainHotMic };
}
